import { useGetAccessTokenPayload, useGetUserInfo, useLogout } from 'auth';
import { FormikProvider, useFormik } from 'formik';
import { UpdateUserRequest } from 'identity-center-api';
import { ChangeEventHandler, useCallback, useState } from 'react';
import toast from 'react-hot-toast';
import * as yup from 'yup';
import { checkAccessEnv } from '@/modules/auth/utils/checkAccessEnv.ts';
import {
  useReset2FA,
  useToggle2FA,
  useUpdateUserIdentityCenterProfile,
} from '@/modules/common/queries/profileSettings.ts';
import { Avatar, Button, TextInputField, Toggle, TooltipNew } from 'ui';
import { AlertOrangeIcon } from 'ui/icons';

const profileNameValidationSchema = yup.object<UpdateUserRequest>({
  firstName: yup
    .string()
    .trim()
    .min(3, 'Must be at least 3 characters')
    .matches(
      /^[a-zA-Z\s'-]+$/,
      'Invalid format, only allowed: letters, apostrophes, spaces, hyphens',
    )
    .required('This field is required'),
  lastName: yup
    .string()
    .trim()
    .min(3, 'Must be at least 3 characters')
    .matches(
      /^[a-zA-Z\s'-]+$/,
      'Invalid format, only allowed: letters, apostrophes, spaces, hyphens',
    )
    .required('This field is required'),
});

const ProfileTabContent = () => {
  const { avatar, first_name, last_name, email } = useGetUserInfo();
  const { isEnable2FA, refresh } = useGetAccessTokenPayload();
  const logout = useLogout();
  const [isToggle2FA, setIsToggle2FA] = useState(false);

  const handleOnSuccessReset2FA = useCallback(async () => {
    setIsToggle2FA(true);
    toast(
      t => (
        <span className="flex flex-col justify-center items-center gap-2 text-sm">
          <span>
            <img src={AlertOrangeIcon} className="w-8 h-8" alt="alert" />
          </span>
          <div>
            To set up your new 2FA, please log out and log in again to see a 2FA set up screen.
          </div>
          <button
            className="px-2 py-1 border rounded !hover:!border-blue-800"
            onClick={() => {
              toast.dismiss(t.id);
              logout(window.location.origin);
            }}
          >
            Logout
          </button>
        </span>
      ),
      {
        duration: 10000,
        id: 'reset',
      },
    );
    await refresh();
    setIsToggle2FA(false);
  }, [logout, refresh]);

  const { mutateAsync: reset2FA, isLoading: isLoadingReset2FA } = useReset2FA({
    onSuccess: handleOnSuccessReset2FA,
  });

  const handleOnSuccessToggle2FA = useCallback(
    async (enabled: boolean) => {
      setIsToggle2FA(true);
      if (enabled) {
        reset2FA();
      } else {
        toast.dismiss('reset');
        await refresh();
      }
      setIsToggle2FA(false);
    },
    [refresh, reset2FA],
  );

  const { mutateAsync: toggle2FA, isLoading: isLoadingToggle2FA } = useToggle2FA({
    onSuccess: handleOnSuccessToggle2FA,
  });

  const { mutateAsync: updateSettings, isLoading } = useUpdateUserIdentityCenterProfile();

  const nameFormik = useFormik<UpdateUserRequest>({
    initialValues: {
      firstName: first_name ?? '',
      lastName: last_name ?? '',
    },
    enableReinitialize: true,
    validationSchema: profileNameValidationSchema,
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: async (values, { resetForm }) => {
      const updatedValues = {
        firstName: values.firstName?.trim() ?? '',
        lastName: values.lastName?.trim() ?? '',
      };

      await updateSettings(updatedValues);
      resetForm({ values: updatedValues });
    },
  });

  const handleLoadFile: ChangeEventHandler<HTMLInputElement> = useCallback(
    event => {
      const file = event.target.files?.[0];

      if (!file) return;

      const reader = new FileReader();

      reader.onload = function () {
        event.target.value = '';
        if (typeof reader.result === 'string') {
          updateSettings({ avatar: reader.result });
        }
      };
      reader.onerror = function (error) {
        event.target.value = '';
        console.log('Error: ', error);
      };

      reader.readAsDataURL(file);
    },
    [updateSettings],
  );

  const handleReset2fa = () => {
    reset2FA();
  };

  const handleToggle2FA = useCallback(() => {
    toggle2FA({ enabled: !isEnable2FA });
  }, [isEnable2FA, toggle2FA]);

  const handleRemoveAvatar = useCallback(() => {
    updateSettings({ avatar: null });
  }, [updateSettings]);

  const get2FADisabledState = (isEnabled: boolean): boolean => {
    // DEV & STAGING → you can always change
    if (checkAccessEnv(['dev', 'staging'])) {
      return false;
    }

    // PRODUCTION → can be turned on, but not turned off
    // if disabled, allow clicks
    if (!isEnabled) return false;

    // if enabled, block (cannot be disabled)
    return true;
  };

  return (
    <div className="flex flex-col gap-10 w-full">
      <FormikProvider value={nameFormik}>
        <form className="flex flex-col gap-4" onSubmit={nameFormik.handleSubmit}>
          <span className="font-semibold font-[Inter]">Name</span>
          <div className="flex gap-4 flex-col sm:flex-row">
            <TextInputField name="firstName" label="First name" disabled={isLoading} />
            <TextInputField name="lastName" label="Last name" disabled={isLoading} />
          </div>
          {nameFormik.dirty && (
            <div className="w-fit">
              <Button type="submit" disabled={isLoading} loading={isLoading}>
                Save
              </Button>
            </div>
          )}
        </form>
      </FormikProvider>

      <div className="flex flex-col gap-1">
        <span className="font-semibold font-[Inter]">Email</span>
        <div className="text-[15px] leading-5">{email}</div>
      </div>

      <div className="flex flex-col gap-6">
        <span className="font-semibold font-[Inter]">Avatar</span>
        <div className="flex gap-6 items-center justify-between w-full">
          <div>
            <Avatar
              className="w-[56px] h-[56px]"
              src={avatar}
              firstName={first_name}
              lastName={last_name}
            />
          </div>

          <div className="flex gap-6 items-center">
            <div className="w-fit">
              <Button variant="plain" className="cursor-pointer !p-[0px]" disabled={isLoading}>
                <label
                  htmlFor="upload-photo"
                  className="cursor-pointer flex items-center justify-center py-[9px] px-[16px] w-[100%] h-[100%] m-0"
                >
                  {avatar ? 'Edit' : 'Add photo'}
                </label>
                <input
                  type="file"
                  name="photo"
                  id="upload-photo"
                  accept="image/png, image/jpg, image/jpeg"
                  className="w-0 h-0 opacity-0 cursor-pointer"
                  onChange={handleLoadFile}
                />
              </Button>
            </div>

            {!!avatar && (
              <div className="w-fit">
                <Button variant="plain" disabled={isLoading} onClick={handleRemoveAvatar}>
                  Remove
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-6">
        <span className="font-semibold font-[Inter]">2FA Settings</span>

        <div className="flex gap-6 items-center justify-between w-full">
          <TooltipNew
            text={`Please talk to your administrator to turn ${isEnable2FA ? 'off' : 'on'} 2FA`}
            disabled={!get2FADisabledState(!!isEnable2FA)}
          >
            <Toggle
              title="Require 2FA on each login via password"
              enabled={!!isEnable2FA}
              disabled={
                get2FADisabledState(!!isEnable2FA) ||
                isLoadingToggle2FA ||
                isLoadingReset2FA ||
                isToggle2FA
              }
              onChange={handleToggle2FA}
              variant={'blue'}
            />
          </TooltipNew>

          {isEnable2FA === true && (
            <div className="w-fit">
              <Button
                variant="plain"
                disabled={isLoadingToggle2FA || isLoadingReset2FA || isToggle2FA}
                loading={isLoadingReset2FA}
                onClick={handleReset2fa}
              >
                Reset
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfileTabContent;
